// 엑셀 내려받기 2종 (2026-09-04 대표 지시)
//   A) 월별 정산자료(세무사용) — 거래명세서·세금계산서 목록 페이지 공통 버튼
//   B) 보철 주문내역 — 웹 의뢰서(workOrders)만, 거래명세서 목록 페이지 버튼
// 원칙: 각 페이지가 이미 쓰는 컬렉션·쿼리·권한만 재사용 (신규 컬렉션 없음). A 파일에는 환자정보를 넣지 않는다.
// 파일명에는 환자명을 쓰지 않는다. 실패는 호출부에서 alert + console.error (조용한 실패 금지).
import * as XLSX from 'xlsx';
import { collection, query, where, getDocs, getDoc, doc, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase/config';

// ── 공용 헬퍼 ──────────────────────────────────────────────────────────
// Timestamp / Date / ISO 문자열 / yyyy-mm-dd 혼재 흡수
export const toJsDate = (v) => {
    if (!v) return null;
    if (v.toDate) return v.toDate();
    if (v instanceof Date) return isNaN(v) ? null : v;
    if (typeof v === 'object' && typeof v.seconds === 'number') return new Date(v.seconds * 1000);
    const d = new Date(v);
    return isNaN(d) ? null : d;
};
const fmtDate = (v) => {
    const d = toJsDate(v);
    if (!d) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtDateTime = (v) => {
    const d = toJsDate(v);
    if (!d) return '';
    return `${fmtDate(d)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const num = (v) => (Number(v) || 0);
// 파일명 안전화 — 경로 문자·공백 제거
const safeName = (s) => String(s || '').replace(/[\\/:*?"<>|\s]+/g, '').slice(0, 30) || '회사';

// 공정 처리 주체 (stageSource) → 한글. 값이 없거나 모르는 값은 빈칸(지어내지 않음).
const stageSourceLabel = (src) => ({ auto_due: '자동', manual: '수동', agent: '프로그램', auto: '프로그램' }[src] || (src ? String(src) : ''));

export const companyNameOf = (user) => user?.businessName || user?.companyName || user?.name || '';
export const isClinicUser = (user) => ['dental', 'clinic'].includes(user?.businessType || user?.companyBusinessType || '');
// 기공소 직원 계정 = companyId 있고 회사가 치과가 아님 → rules docReadAccess(대표 또는 치과 소속 직원만)에 걸려 읽기 거부.
// 권한 오류 대신 미리 안내한다 (검토 B-3).
export const isLabStaff = (user) => !!user?.companyId && user.companyId !== user?.uid && !isClinicUser(user);
export const LAB_STAFF_EXPORT_MSG = '대표 계정만 내려받을 수 있습니다.\n(기공소 직원 계정은 명세서·세금계산서·의뢰서 자료 읽기가 제한됩니다)';

// 지난달 'YYYY-MM' (세무 자료 기본값)
export const lastMonthValue = () => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
export const toDateInput = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const SETTLEMENT_LABEL = { pending: '정산 대기', disputed: '수정 요청', settled: '정산 완료' };
const TAX_STATUS_LABEL = {
    draft: '작성 중', issued: '발행됨', sent_to_nts: '국세청 전송', nts_confirmed: '국세청 확인',
    cancelled: '취소', canceled: '취소', error: '오류',
};
const STATEMENT_STATUS_LABEL = { draft: '작성 중', confirmed: '발행됨' };

// 시트 생성 — 헤더 + 행(배열) + 열 너비
const makeSheet = (header, rows, widths) => {
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    if (widths) ws['!cols'] = widths.map(w => ({ wch: w }));
    return ws;
};

// ── A) 월별 정산자료(세무사용) ──────────────────────────────────────────
/**
 * @param {object} user  로그인 사용자(companyId·businessType 포함)
 * @param {string} monthValue 'YYYY-MM'
 * @returns {{ statementCount:number, invoiceCount:number, fileName:string }}
 *   0건이면 파일을 만들지 않고 statementCount·invoiceCount 0으로 반환 (호출부가 안내)
 */
export async function exportTaxMonthlyReport(user, monthValue) {
    const uid = user?.companyId || user?.uid;
    if (!uid) throw new Error('로그인 정보가 없습니다.');
    if (isLabStaff(user)) throw new Error(LAB_STAFF_EXPORT_MSG);
    const [yy, mm] = String(monthValue || '').split('-').map(Number);
    if (!yy || !mm) throw new Error('월을 선택해주세요.');
    const start = new Date(yy, mm - 1, 1);
    const end = new Date(yy, mm, 1);           // 미포함 경계
    const inMonth = (v) => { const d = toJsDate(v); return !!d && d >= start && d < end; };
    const clinic = isClinicUser(user);

    // 1) 거래명세서 — TransactionStatementList와 같은 쿼리(from/toUserId + createdAt 범위, endDate 범위 병행)
    //    발행일(publishedAt) 기준으로 월을 자르되, createdAt이 이어쓰기로 이르게 남은 문서를 놓치지 않도록
    //    목록 페이지와 같은 ±62일 여유 조회 후 클라이언트에서 월 판정.
    const stRef = collection(db, 'transactionStatements');
    const MARGIN_MS = 62 * 24 * 3600 * 1000;
    const qStart = new Date(start.getTime() - MARGIN_MS);
    const qEnd = new Date(end.getTime() + MARGIN_MS);
    const sideField = clinic ? 'toUserId' : 'fromUserId';   // 기공소=발행분, 치과=받은 것
    const [cSnap, eSnap] = await Promise.all([
        getDocs(query(stRef, where(sideField, '==', uid),
            where('createdAt', '>=', qStart), where('createdAt', '<=', qEnd), orderBy('createdAt', 'desc'))),
        getDocs(query(stRef, where(sideField, '==', uid),
            where('endDate', '>=', qStart), where('endDate', '<=', qEnd), orderBy('endDate', 'desc')))
            .catch(e => { console.error('세무자료: 기간 기준 명세서 조회 실패(작성일 기준만 사용):', e?.code || e?.message); return { docs: [] }; }),
    ]);
    // 월 판정 = 명세서 기간 종료일(endDate, 공급시기)의 월. endDate 없으면 publishedAt||createdAt (QA FAIL 1 반영 — 발행일 기준은 목록과 불일치)
    const monthKeyDate = (s) => toJsDate(s.endDate) || toJsDate(s.publishedAt) || toJsDate(s.createdAt);
    const stMap = new Map();
    [...cSnap.docs, ...eSnap.docs].forEach(d => {
        if (stMap.has(d.id)) return;
        const s = { id: d.id, ...d.data() };
        if (s.status === 'draft') return;                       // 작성 중 제외
        if (!clinic && s.deletedBySender) return;               // 발행처(내)가 삭제한 발행본 제외 (목록과 동일)
        if (!inMonth(monthKeyDate(s))) return;
        stMap.set(d.id, s);
    });
    const statements = Array.from(stMap.values())
        .sort((a, b) => (monthKeyDate(a) || 0) - (monthKeyDate(b) || 0));

    // 2) 세금계산서 — TaxInvoiceList와 같은 쿼리(createdBy / buyerInfo.userId, 회사ID·본인uid 둘 다)
    const tiRef = collection(db, 'taxInvoices');
    const ids = new Set([uid, auth.currentUser?.uid].filter(Boolean));
    const tiMap = new Map();
    for (const id of ids) {
        const q = clinic
            ? query(tiRef, where('buyerInfo.userId', '==', id))
            : query(tiRef, where('createdBy', '==', id));
        try {
            const snap = await getDocs(q);
            snap.docs.forEach(d => {
                if (tiMap.has(d.id)) return;
                const inv = { id: d.id, ...d.data() };
                if (inv.status === 'draft') return;
                tiMap.set(d.id, inv);   // 취소·오류는 월 판정 뒤 제외(건수만 요약에 표기)
            });
        } catch (e) {
            console.error('세무자료: 세금계산서 조회 실패:', id, e?.code || e?.message);
            throw e;
        }
    }
    // 연결 조회용(월 밖 계산서도 명세서 행의 "세금계산서 번호"에 쓰기 위해 전체 보관)
    // 유효 계산서만 명세서 연결·시트·합계에 쓴다 (검토 B-2: 취소·오류 제외, 제외 건수는 요약에 표기)
    // 팝빌 취소는 electronicStatus 만 'cancelled'(status 는 'issued' 그대로) — 원장 isCountableTaxInvoice 와 같은 기준 (P2-9, 2026-10-24)
    const isVoidInvoice = (inv) => ['cancelled', 'canceled', 'error'].includes(inv.status) || inv.electronicStatus === 'cancelled';
    const allInvoices = Array.from(tiMap.values()).filter(inv => !isVoidInvoice(inv));
    const excludedInvoiceCount = Array.from(tiMap.values())
        .filter(inv => isVoidInvoice(inv) && inMonth(inv.issueDate || inv.createdAt)).length;
    const invoices = allInvoices
        .filter(inv => inMonth(inv.issueDate || inv.createdAt))
        .sort((a, b) => (toJsDate(a.issueDate || a.createdAt) || 0) - (toJsDate(b.issueDate || b.createdAt) || 0));

    if (statements.length === 0 && invoices.length === 0) {
        return { statementCount: 0, invoiceCount: 0, fileName: '' };
    }

    // 명세서 ↔ 계산서 연결: statement.taxInvoiceId ↔ invoice.id, invoice.linkedStatementId ↔ statement.id
    const invById = new Map(allInvoices.map(i => [i.id, i]));
    const invByStatementId = new Map();
    allInvoices.forEach(i => { if (i.linkedStatementId && !invByStatementId.has(i.linkedStatementId)) invByStatementId.set(i.linkedStatementId, i); });
    const stById = new Map(statements.map(s => [s.id, s]));
    // 계산서에 연결된 명세서가 이 달 목록에 없으면 번호만 개별 조회 (같은 컬렉션·본인 권한)
    const missingStIds = Array.from(new Set(invoices.map(i => i.linkedStatementId).filter(id => id && !stById.has(id))));
    await Promise.all(missingStIds.map(async (id) => {
        try {
            const snap = await getDoc(doc(db, 'transactionStatements', id));
            if (snap.exists()) stById.set(id, { id, ...snap.data() });
        } catch (e) {
            console.error('세무자료: 연결 명세서 조회 실패(번호 빈칸 처리):', id, e?.code || e?.message);
        }
    }));

    // ── 시트 1: 거래명세서 ──
    // 공급가액 = 명세서 totalAmount(명세서는 부가세 별도 금액으로 저장됨).
    // 부가세·합계 = 연결 세금계산서가 있을 때만 그 값. 없으면 세액 빈칸·합계=공급가액 (검토 B-1: 임의 10% 산정 금지).
    const stRows = [];
    let stSupply = 0, stTax = 0, stTotal = 0;
    statements.forEach(s => {
        const linked = (s.taxInvoiceId && invById.get(s.taxInvoiceId)) || invByStatementId.get(s.id) || null;
        const supply = num(s.totalAmount);
        const tax = linked ? num(linked.taxAmount) : null;
        const total = linked ? supply + num(linked.taxAmount) : supply;
        stSupply += supply; stTax += (tax || 0); stTotal += total;
        const partnerName = clinic ? (s.fromUserName || s.supplierInfo?.businessName || '') : (s.toUserName || '');
        // 거래처 사업자번호: 치과=명세서 supplierInfo, 기공소=연결 세금계산서의 공급받는자 정보(명세서에는 미저장)
        const partnerBiz = clinic ? (s.supplierInfo?.businessNumber || '') : (linked?.buyerInfo?.bizNumber || '');
        const taxStatus = linked ? (TAX_STATUS_LABEL[linked.status] || linked.status || '') : (TAX_STATUS_LABEL[s.taxInvoiceStatus] || s.taxInvoiceStatus || '');
        stRows.push([
            fmtDate(s.publishedAt || s.createdAt),
            `${fmtDate(s.startDate)}~${fmtDate(s.endDate)}`,
            s.statementNumber || '',
            partnerName,
            partnerBiz,
            supply, tax === null ? '' : tax, total,
            STATEMENT_STATUS_LABEL[s.status] || s.status || '',
            SETTLEMENT_LABEL[s.settlementStatus] || (s.settlementStatus || ''),
            fmtDate(s.settledAt),
            linked?.invoiceNumber || '',
            taxStatus,
            linked ? '' : '계산서 미발행',
        ]);
    });
    stRows.push(['합계', '', `${statements.length}건`, '', '', stSupply, stTax, stTotal, '', '', '', '', '', '']);
    const wsSt = makeSheet(
        ['발행일', '명세서 기간', '명세서 번호', '거래처 상호', '사업자번호', '공급가액', '부가세', '합계', '명세서 상태', '정산 상태', '정산 확정일', '세금계산서 번호', '세금계산서 상태', '비고'],
        stRows, [12, 22, 18, 22, 14, 14, 12, 14, 10, 10, 12, 20, 12, 12]);

    // ── 시트 2: 세금계산서 ──
    const tiRows = [];
    let tiSupply = 0, tiTax = 0, tiTotal = 0;
    invoices.forEach(inv => {
        const supply = num(inv.supplyAmount), tax = num(inv.taxAmount), total = num(inv.totalAmount) || (supply + tax);
        tiSupply += supply; tiTax += tax; tiTotal += total;
        const partner = clinic ? inv.supplierInfo : inv.buyerInfo;
        const linkedSt = inv.linkedStatementId ? stById.get(inv.linkedStatementId) : null;
        tiRows.push([
            fmtDate(inv.issueDate || inv.createdAt),
            inv.invoiceNumber || '',
            partner?.name || '',
            partner?.bizNumber || '',
            supply, tax, total,
            inv.isElectronic ? '전자' : '수기',
            inv.isTaxExempt ? '면세' : '과세',
            TAX_STATUS_LABEL[inv.status] || inv.status || '',
            linkedSt?.statementNumber || '',
        ]);
    });
    tiRows.push(['합계', `${invoices.length}건`, '', '', tiSupply, tiTax, tiTotal, '', '', '', '']);
    const wsTi = makeSheet(
        ['발행일', '계산서 번호', '거래처 상호', '사업자번호', '공급가액', '세액', '합계', '전자발행', '과세 구분', '상태', '연결 명세서 번호'],
        tiRows, [12, 18, 22, 14, 14, 12, 14, 8, 8, 12, 18]);

    // ── 시트 3: 요약 ──
    const company = companyNameOf(user);
    const summary = [
        ['항목', '값'],
        ['회사', company],
        ['구분', clinic ? '받은 명세서·계산서 기준(치과)' : '발행 명세서·계산서 기준(기공소)'],
        ['대상 월', `${yy}년 ${mm}월`],
        ['기준', '명세서 기간(공급시기) 월 — 기간 종료일 기준. 세금계산서는 발행일(issueDate) 기준'],
        ['거래명세서 건수', statements.length],
        ['거래명세서 공급가액 합계', stSupply],
        ['거래명세서 부가세 합계', stTax],
        ['거래명세서 합계', stTotal],
        ['세금계산서 건수', invoices.length],
        ['세금계산서 공급가액 합계', tiSupply],
        ['세금계산서 세액 합계', tiTax],
        ['세금계산서 합계', tiTotal],
        ['명세서-계산서 합계 차액', stTotal - tiTotal],
        ['취소·오류 제외', `${excludedInvoiceCount}건`],
        [],
        ['안내', '명세서 부가세·합계는 연결된 세금계산서가 있을 때만 그 값이며, 없으면 세액 빈칸·합계=공급가액(비고 "계산서 미발행")입니다. 차액은 이 기준으로 계산했습니다.'],
        ['', '이 파일에는 환자 정보가 들어 있지 않습니다. 작성 시각: ' + fmtDateTime(new Date())],
    ];
    const wsSum = XLSX.utils.aoa_to_sheet(summary);
    wsSum['!cols'] = [{ wch: 26 }, { wch: 60 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsSt, '거래명세서');
    XLSX.utils.book_append_sheet(wb, wsTi, '세금계산서');
    XLSX.utils.book_append_sheet(wb, wsSum, '요약');
    const fileName = `덴클_정산자료_${safeName(company)}_${yy}-${String(mm).padStart(2, '0')}.xlsx`;
    XLSX.writeFile(wb, fileName);
    return { statementCount: statements.length, invoiceCount: invoices.length, fileName };
}

// ── B) 보철 주문내역 (웹 의뢰서만) ─────────────────────────────────────
const EXCLUDED_STATUS = new Set(['cancelled', 'canceled', 'rejected']);
const EXCLUDED_SOURCE = new Set(['agent_auto', 'dentweb', 'email', 'r2cad']);   // 수집 자동등록 · 이메일 의뢰서(OrderList 이메일 판정 배열과 동일)
/**
 * @param {object} user
 * @param {string} startInput 'YYYY-MM-DD'
 * @param {string} endInput   'YYYY-MM-DD' (포함)
 * @returns {{ orderCount:number, toothCount:number, fileName:string }} 0건이면 파일 미생성
 */
export async function exportOrderHistory(user, startInput, endInput) {
    const uid = user?.companyId || user?.uid;
    if (!uid) throw new Error('로그인 정보가 없습니다.');
    if (isLabStaff(user)) throw new Error(LAB_STAFF_EXPORT_MSG);
    const start = new Date(`${startInput}T00:00:00`);
    const endInc = new Date(`${endInput}T00:00:00`);
    if (isNaN(start) || isNaN(endInc)) throw new Error('기간을 선택해주세요.');
    if (endInc < start) throw new Error('종료일이 시작일보다 앞설 수 없습니다.');
    const end = new Date(endInc.getFullYear(), endInc.getMonth(), endInc.getDate() + 1);   // 미포함 경계
    const clinic = isClinicUser(user);

    // 치과=fromUserId 본인(회사), 기공소=toUserId 본인(회사). 인덱스 (from/toUserId, createdAt DESC) 존재.
    const sideField = clinic ? 'fromUserId' : 'toUserId';
    const snap = await getDocs(query(collection(db, 'workOrders'),
        where(sideField, '==', uid),
        where('createdAt', '>=', start), where('createdAt', '<', end),
        orderBy('createdAt', 'desc')));

    const orders = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(o => o.type !== 'message')
        .filter(o => !EXCLUDED_SOURCE.has(o.source || ''))
        .filter(o => !o.isOutsource)
        .filter(o => !EXCLUDED_STATUS.has(o.status || ''))
        .sort((a, b) => (toJsDate(a.createdAt) || 0) - (toJsDate(b.createdAt) || 0));

    if (orders.length === 0) return { orderCount: 0, toothCount: 0, fileName: '' };

    const rows = [];
    let toothCount = 0;
    orders.forEach(o => {
        const partner = clinic ? (o.toUserName || '') : (o.fromUserName || '');
        const base = [
            fmtDate(o.createdAt),
            fmtDate(o.dueDate),
            o.orderNumber || '',
            partner,
            o.patientName || '',
        ];
        const tail = [
            o.processStage || '',            // 공정 상태 원값 그대로
            fmtDateTime(o.receivedAt),       // 치과 도착 확인(수동) 일시 — 2026-09-29부터 새로 기록되지 않음(과거 기록만)
            stageSourceLabel(o.stageSource), // 공정 처리 주체: 자동(납기 경과 auto_due)/수동/프로그램 (증빙용, 법무 검토 항목 3)
            o.isRemake ? 'Y' : '',
            o.isRemake ? (o.remakeType || '') : '',
        ];
        const items = Array.isArray(o.items) ? o.items : [];
        if (items.length === 0) {
            rows.push([...base, '', '', '', '', '', 1, ...tail]);   // tail 4열(공정 상태/도착 확인/공정 처리/리메이크/구분)
            return;
        }
        [...items]
            .sort((a, b) => (num(a.toothNumber) - num(b.toothNumber)))
            .forEach(it => {
                if (it.toothNumber) toothCount += 1;
                rows.push([
                    ...base,
                    it.toothNumber ? `#${it.toothNumber}` : '',
                    it.prosthesis || '',
                    it.material || '',
                    it.shade || '',
                    it.implant ? `Y (${it.implant})` : '',
                    1,
                    ...tail,
                ]);
            });
    });
    rows.push(['합계', '', `의뢰서 ${orders.length}건`, '', '', `치아 ${toothCount}개`, '', '', '', '', rows.length, '', '', '', '', '']);

    const header = ['주문일', '납기일', '의뢰서 번호', clinic ? '기공소 상호' : '치과 상호', '환자명', '치식(FDI)', '보철 종류', '재료', '쉐이드', '임플란트', '수량', '공정 상태', '도착 확인(수동) 일시', '공정 처리', '리메이크', '리메이크 구분'];
    const ws = makeSheet(header, rows, [12, 12, 18, 20, 12, 10, 16, 12, 10, 14, 6, 12, 18, 10, 8, 12]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '주문내역');
    const company = companyNameOf(user);
    const fileName = `덴클_주문내역_${safeName(company)}_${startInput.replace(/-/g, '')}-${endInput.replace(/-/g, '')}.xlsx`;
    XLSX.writeFile(wb, fileName);
    return { orderCount: orders.length, toothCount, fileName };
}
